const DATE_FORMAT = new Intl.DateTimeFormat("en-US", {
  weekday: "short",
  day: "numeric",
  month: "short",
  year: "numeric",
});

const TIME_FORMAT = new Intl.DateTimeFormat("en-US", {
  hour: "numeric",
  minute: "2-digit",
});

/** Does this naive datetime carry a real, known time-of-day?
 *
 * A bare `T00:00` is treated as time-unknown, not midnight: the extractor synthesizes it for
 * teaser/production-run cards that print no time, and genuine midnight concerts don't exist in
 * this repertoire. Showing "12:00 AM" on those rows is the classic scraped-data tell — so we
 * render the date only. (A real late-night event would need a corroborating non-zero time.) */
function hasKnownTime(naive: string): boolean {
  return naive.includes("T") && naive.length > 10 && naive.slice(11, 16) !== "00:00";
}

/** Format a concert date string for display. */
export function formatDate(dateStr: string | null): string {
  if (!dateStr) return "Date TBA";

  try {
    // Strip timezone offset so wall-clock time is shown regardless of viewer's locale
    const naive = dateStr.replace(/([+-]\d{2}:?\d{2}|Z)$/, "");
    const hasTime = hasKnownTime(naive);
    const date = new Date(naive + (naive.includes("T") ? "" : "T12:00"));

    if (isNaN(date.getTime())) return dateStr;

    const datePart = DATE_FORMAT.format(date);
    if (!hasTime) return datePart;

    const timePart = TIME_FORMAT.format(date);
    return `${datePart}, ${timePart}`;
  } catch {
    return dateStr;
  }
}

export interface DateParts {
  weekday: string;
  day: string;
  month: string;
  year: string;
  time: string | null;
}

/** Split a concert date into display parts for the card's date line.
 *  Returns null when the date is missing or unparseable. */
export function formatDateParts(dateStr: string | null): DateParts | null {
  if (!dateStr) return null;
  try {
    const naive = dateStr.replace(/([+-]\d{2}:?\d{2}|Z)$/, "");
    const hasTime = hasKnownTime(naive);
    const date = new Date(naive + (naive.includes("T") ? "" : "T12:00"));
    if (isNaN(date.getTime())) return null;

    return {
      weekday: new Intl.DateTimeFormat("en-US", { weekday: "short" }).format(date),
      day: String(date.getDate()),
      month: new Intl.DateTimeFormat("en-US", { month: "short" }).format(date),
      year: String(date.getFullYear()),
      time: hasTime ? TIME_FORMAT.format(date) : null,
    };
  } catch {
    return null;
  }
}

const PROVENANCE_DATE_YEAR = new Intl.DateTimeFormat("en-US", {
  day: "numeric",
  month: "short",
  year: "numeric",
});
const PROVENANCE_FULL = new Intl.DateTimeFormat("en-US", { dateStyle: "long" });

export interface Provenance {
  /** Short line for the card, e.g. "Program as of Jul 11, 2026". Always carries the
   *  year — the list spans centuries, so a yearless date is ambiguous. */
  label: string;
  /** Tooltip spelling out what the date does and doesn't promise. */
  detail: string;
  /** Machine-readable original, for <time datetime>. */
  iso: string;
}

/** Describe when this listing's data was last observed at the source.
 *
 *  `checkedAt` is when we last *read the venue's page*, so it is an upper bound on what we
 *  can possibly know: a program the presenter announced afterwards is invisible to us until
 *  the next crawl. Saying "as of <date>" is the honest version of that; showing the program
 *  bare would imply a currency we can't back up. Returns null on missing/unparseable input,
 *  in which case the card simply says nothing rather than guessing.
 *
 *  Deliberately NO staleness tiers or warning treatment: we make no promises about how
 *  often a page gets re-read, so an old read date is a disclosure, not an alarm. */
export function formatProvenance(
  checkedAt: string | null | undefined,
  hasProgram: boolean,
): Provenance | null {
  if (!checkedAt) return null;
  const date = new Date(checkedAt);
  if (isNaN(date.getTime())) return null;

  const subject = hasProgram ? "Program" : "Details";
  return {
    label: `${subject} as of ${PROVENANCE_DATE_YEAR.format(date)}`,
    detail:
      `We last read this concert's page on ${PROVENANCE_FULL.format(date)}. ` +
      "Anything the presenter has changed since then isn't reflected here.",
    iso: checkedAt,
  };
}

/** Truncate text with ellipsis. */
export function truncate(text: string, maxLen: number): string {
  if (text.length <= maxLen) return text;
  return text.slice(0, maxLen - 1) + "\u2026";
}

/** Intl's ISO-3166 code → country name table. Built once; `undefined` on the handful of
 *  engines that don't implement DisplayNames, in which case callers keep their fallback. */
const COUNTRY_NAMES: Intl.DisplayNames | undefined = (() => {
  try {
    return new Intl.DisplayNames(["en"], { type: "region" });
  } catch {
    return undefined;
  }
})();

/** Display name for a country code. The export sets `country_name` from the cities table,
 *  which is missing for ~40 cities (they carry the bare code) — rather than showing the
 *  reader "Saint John's, AG", fall back to Intl's own table. `given` still wins when it is
 *  a real name, so the export stays the source of truth where it has one. */
export function countryDisplayName(code: string, given?: string | null): string {
  if (given && given !== code) return given;
  if (!code) return given ?? "";
  // Intl.DisplayNames throws on anything that isn't a region code — a stale bucket key
  // from another dimension must degrade to text, never abort a render.
  try {
    return COUNTRY_NAMES?.of(code.toUpperCase()) ?? given ?? code;
  } catch {
    return given ?? code;
  }
}

/** The label on an opt-in event's card and popup. Kept to what the row is called, not a claim that
 *  it is not classical: about 1 in 25 of these hides is a real concert the classifier got wrong. */
const EXTRA_CLASS_LABELS: Record<string, string> = {
  other_music: "Other music",
  ballet_dance: "Ballet & dance",
  kids_nonmusic: "Kids & family",
  spoken_theater: "Theatre",
  comedy: "Comedy",
  film_screening: "Film",
  talk_tour_workshop: "Talk or tour",
  other_nonmusic: "Other event",
  academic: "Student recital",
  tourist: "Tourist show",
  civic: "Theatre season",
};

export function extraClassLabel(cls: string): string {
  return EXTRA_CLASS_LABELS[cls] ?? "Other event";
}
